import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { Plus, Trash2, Save, FileDown, ArrowLeft } from "lucide-react";
import Sidebar from "@/components/Sidebar";
import HotelCategorySelect from "@/components/HotelCategorySelect";
import HotelPlaceSelect from "@/components/HotelPlaceSelect";
import CustomerPicker from "@/components/CustomerPicker";
import { ROOM_CATEGORIES } from "@/components/HotelForm";
import { api } from "@/lib/api";

const INCLUSION_OPTIONS = [
  "Room Only",
  "Bed and Breakfast",
  "Breakfast + Dinner",
  "Breakfast + Lunch + Dinner",
  "All Inclusive",
];

// Compact dropdown with "Other…" escape hatch. Keeps legacy free-text values working.
function PickOrType({ options, value, onChange, testid, placeholder = "Select…" }) {
  const isCustom = value && !options.includes(value);
  const [other, setOther] = React.useState(isCustom);
  React.useEffect(() => { setOther(value && !options.includes(value)); }, [value, options]);
  if (other) {
    return (
      <div className="flex gap-1.5">
        <input
          data-testid={`${testid}-other`}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder="Custom"
          className="flex-1 border border-border rounded px-2 py-1.5 text-sm"
        />
        <button type="button" onClick={() => { setOther(false); onChange(""); }}
          className="text-[10px] text-slate-500 hover:text-slate-800 underline underline-offset-2">list</button>
      </div>
    );
  }
  return (
    <select
      data-testid={testid}
      value={value || ""}
      onChange={(e) => {
        if (e.target.value === "__other__") { setOther(true); onChange(""); }
        else onChange(e.target.value);
      }}
      className="w-full border border-border rounded px-2 py-1.5 text-sm bg-white"
    >
      <option value="">{placeholder}</option>
      {options.map((o) => <option key={o} value={o}>{o}</option>)}
      <option value="__other__">Other…</option>
    </select>
  );
}

const DEFAULT_TERMS = [
  "Free Wireless Internet",
  "Check-in time at the hotel is at midnight.",
  "Check-out time: 11:00 AM",
  "Carry valid ID proof and voucher copy is recommended.",
  "Hotels may charge a security deposit at the time of check-in, refundable if unutilized.",
  "Early check-in & late check-out are subject to availability and as per hotel policy.",
  "Any supplementary charges / Gala Dinner / City Tax etc. to be paid directly at the property.",
  "Extra beds are not included unless stated in hotel / booking policies.",
];

const empty = {
  status: "CONFIRMED",
  customer_id: "", customer_ref: null,
  hotel_name: "", hotel_category: "", hotel_address: "", hotel_contact: "", hotel_city: "", hotel_country: "",
  hotel_image_url: "", hotel_place_id: "",
  reference_id: "", supplier_confirmation_no: "",
  pnr: "", issued_on: new Date().toISOString().slice(0, 10),
  rooms: [{ room_type: "Deluxe Room" }],
  check_in_date: "", check_in_time: "14:00",
  check_out_date: "", check_out_time: "12:00",
  nights: 1,
  passengers: [{ name: "", room_type: "Deluxe Room", room_inclusion: "Bed and Breakfast", room_confirmation: "", pax_type: "Adult", status: "CONFIRMED" }],
  terms_conditions: DEFAULT_TERMS,
  footer_phone: "", footer_address: "",
};

function Field({ label, value, onChange, type = "text", testid, className = "" }) {
  return (
    <label className={`block ${className}`}>
      <div className="text-[11px] uppercase tracking-widest text-slate-500 mb-1">{label}</div>
      <input
        data-testid={testid}
        type={type}
        value={value ?? ""}
        onChange={(e) => onChange(e.target.value)}
        className="w-full border border-border rounded px-3 py-2 text-sm focus:border-emerald-500 focus:outline-none"
      />
    </label>
  );
}

export default function VoucherBuilder() {
  const { id } = useParams();
  const nav = useNavigate();
  const [v, setV] = useState(empty);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!id) return;
    api.get(`/vouchers/${id}`).then(({ data }) => setV({ ...empty, ...data }))
      .catch(() => toast.error("Could not load voucher"));
  }, [id]);

  // Auto-compute nights from check-in / check-out
  useEffect(() => {
    if (v.check_in_date && v.check_out_date) {
      const d1 = new Date(v.check_in_date);
      const d2 = new Date(v.check_out_date);
      const diff = Math.max(1, Math.round((d2 - d1) / 86_400_000));
      if (diff !== v.nights) setV((p) => ({ ...p, nights: diff }));
    }
  }, [v.check_in_date, v.check_out_date]); // eslint-disable-line

  const set = (k, val) => setV((p) => ({ ...p, [k]: val }));

  const addRoom = () => setV((p) => ({ ...p, rooms: [...(p.rooms || []), { room_type: "" }] }));
  const updRoom = (i, val) => setV((p) => ({ ...p, rooms: p.rooms.map((r, idx) => idx === i ? { room_type: val } : r) }));
  const rmRoom = (i) => setV((p) => ({ ...p, rooms: p.rooms.filter((_, idx) => idx !== i) }));

  const addPax = () => setV((p) => ({
    ...p, passengers: [...(p.passengers || []), {
      name: "", room_type: p.rooms?.[0]?.room_type || "", room_inclusion: "Bed and Breakfast",
      room_confirmation: p.reference_id || "", pax_type: "Adult", status: "CONFIRMED"
    }]
  }));
  const updPax = (i, k, val) => setV((p) => ({ ...p, passengers: p.passengers.map((x, idx) => idx === i ? { ...x, [k]: val } : x) }));
  const rmPax = (i) => setV((p) => ({ ...p, passengers: p.passengers.filter((_, idx) => idx !== i) }));

  const updTerm = (i, val) => setV((p) => ({ ...p, terms_conditions: p.terms_conditions.map((t, idx) => idx === i ? val : t) }));
  const rmTerm = (i) => setV((p) => ({ ...p, terms_conditions: p.terms_conditions.filter((_, idx) => idx !== i) }));
  const addTerm = () => setV((p) => ({ ...p, terms_conditions: [...p.terms_conditions, ""] }));

  const save = async ({ open = false } = {}) => {
    setSaving(true);
    try {
      let vid = id;
      if (!vid) {
        const { data } = await api.post("/vouchers", v);
        vid = data.id;
        toast.success("Voucher created");
        nav(`/vouchers/${vid}/edit`, { replace: true });
      } else {
        await api.put(`/vouchers/${vid}`, v);
        toast.success("Saved");
      }
      if (open) {
        const res = await api.get(`/vouchers/${vid}/pdf`, { responseType: "blob" });
        window.open(URL.createObjectURL(res.data), "_blank");
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="flex min-h-screen bg-slate-50">
      <Sidebar role="agent" />
      <main className="flex-1 p-8 lg:p-10 max-w-6xl">
        <button onClick={() => nav("/vouchers")} className="inline-flex items-center gap-1 text-sm text-slate-500 hover:text-emerald-700 mb-4">
          <ArrowLeft size={14} /> Back to vouchers
        </button>
        <div className="flex items-center justify-between mb-6">
          <div>
            <div className="overline text-[10px] tracking-[0.28em] text-emerald-700">
              {id ? "EDIT VOUCHER" : "NEW VOUCHER"}
            </div>
            <h1 className="font-editorial text-3xl text-slate-900 mt-1">Hotel voucher builder</h1>
          </div>
          <div className="flex gap-2">
            <button data-testid="voucher-save-btn" onClick={() => save()} disabled={saving}
              className="inline-flex items-center gap-2 border border-emerald-600 text-emerald-700 px-4 py-2 rounded text-sm font-semibold hover:bg-emerald-50">
              <Save size={14} /> {saving ? "Saving…" : "Save"}
            </button>
            <button data-testid="voucher-pdf-btn" onClick={() => save({ open: true })} disabled={saving}
              className="inline-flex items-center gap-2 bg-[#16A34A] hover:bg-emerald-700 text-white px-4 py-2 rounded text-sm font-semibold">
              <FileDown size={14} /> Save & Download PDF
            </button>
          </div>
        </div>

        {/* Hotel Info */}
        <section className="bg-white border border-border rounded p-5 mb-5">
          <h2 className="text-sm font-semibold text-slate-900 mb-4 uppercase tracking-widest text-emerald-700">Hotel Information</h2>
          <div className="mb-4">
            <HotelPlaceSelect
              value={v.hotel_place_id ? v.hotel_name : ""}
              onPick={(picked) => setV((p) => ({
                ...p,
                hotel_place_id: picked.place_id || "",
                hotel_name: picked.name || p.hotel_name,
                hotel_address: picked.address || p.hotel_address,
                hotel_city: picked.city || p.hotel_city,
                hotel_country: picked.country || p.hotel_country,
                hotel_contact: picked.phone || p.hotel_contact,
                hotel_image_url: picked.image || p.hotel_image_url,
              }))}
              testid="v-hotel-google"
            />
            {v.hotel_place_id && (
              <div className="text-[11px] text-emerald-700 mt-1.5 flex items-center gap-1">
                ✓ Linked to Google. PDF will always use the latest details from Google.
                <button
                  type="button"
                  data-testid="v-hotel-google-unlink"
                  onClick={() => setV((p) => ({ ...p, hotel_place_id: "" }))}
                  className="ml-2 text-slate-400 hover:text-slate-700 underline underline-offset-2"
                >Unlink</button>
              </div>
            )}
          </div>
          <div className="grid grid-cols-2 gap-4">
            <Field label="Hotel Name" value={v.hotel_name} onChange={(x) => set("hotel_name", x)} testid="v-hotel-name" />
            <Field label="Contact No" value={v.hotel_contact} onChange={(x) => set("hotel_contact", x)} testid="v-hotel-contact" />
            <div>
              <label className="block text-xs uppercase tracking-widest text-slate-500 mb-1.5">Hotel Category</label>
              <HotelCategorySelect value={v.hotel_category} onChange={(x) => set("hotel_category", x)} testid="v-hotel-category" />
            </div>
            <div />
            <Field label="Address" value={v.hotel_address} onChange={(x) => set("hotel_address", x)} testid="v-hotel-addr" className="col-span-2" />
            <Field label="City" value={v.hotel_city} onChange={(x) => set("hotel_city", x)} testid="v-hotel-city" />
            <Field label="Country" value={v.hotel_country} onChange={(x) => set("hotel_country", x)} testid="v-hotel-country" />
            <Field label="Hotel Image URL (optional)" value={v.hotel_image_url} onChange={(x) => set("hotel_image_url", x)} testid="v-hotel-img" className="col-span-2" />
          </div>
        </section>

        {/* Reference & PNR */}
        <section className="bg-white border border-border rounded p-5 mb-5">
          <h2 className="text-sm font-semibold text-emerald-700 mb-4 uppercase tracking-widest">Reference & Confirmation</h2>
          <div className="grid grid-cols-2 gap-4">
            <Field label="PNR" value={v.pnr} onChange={(x) => set("pnr", x)} testid="v-pnr" />
            <Field label="Issued On" type="date" value={v.issued_on} onChange={(x) => set("issued_on", x)} testid="v-issued" />
            <Field label="Reference ID" value={v.reference_id} onChange={(x) => set("reference_id", x)} testid="v-ref-id" />
            <Field label="Supplier Confirmation No" value={v.supplier_confirmation_no} onChange={(x) => set("supplier_confirmation_no", x)} testid="v-sup-conf" />
          </div>
        </section>

        {/* Stay Dates */}
        <section className="bg-white border border-border rounded p-5 mb-5">
          <h2 className="text-sm font-semibold text-emerald-700 mb-4 uppercase tracking-widest">Stay Dates</h2>
          <div className="grid grid-cols-4 gap-4">
            <Field label="Check-in Date" type="date" value={v.check_in_date} onChange={(x) => set("check_in_date", x)} testid="v-ci-date" />
            <Field label="Check-in Time" value={v.check_in_time} onChange={(x) => set("check_in_time", x)} testid="v-ci-time" />
            <Field label="Check-out Date" type="date" value={v.check_out_date} onChange={(x) => set("check_out_date", x)} testid="v-co-date" />
            <Field label="Check-out Time" value={v.check_out_time} onChange={(x) => set("check_out_time", x)} testid="v-co-time" />
          </div>
          <div className="text-xs text-slate-500 mt-2">Nights auto-calculated: <b>{v.nights}</b></div>
        </section>

        {/* Rooms */}
        <section className="bg-white border border-border rounded p-5 mb-5">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-sm font-semibold text-emerald-700 uppercase tracking-widest">Rooms ({v.rooms.length})</h2>
            <button data-testid="v-add-room" onClick={addRoom} className="inline-flex items-center gap-1 text-xs text-[#16A34A] hover:underline"><Plus size={13} /> Add room</button>
          </div>
          <div className="space-y-2">
            {v.rooms.map((r, i) => (
              <div key={i} className="flex items-center gap-2">
                <div className="flex-1">
                  <PickOrType
                    testid={`v-room-${i}`}
                    options={ROOM_CATEGORIES}
                    value={r.room_type}
                    onChange={(val) => updRoom(i, val)}
                    placeholder="Select room category…"
                  />
                </div>
                <button data-testid={`v-room-rm-${i}`} onClick={() => rmRoom(i)} className="p-2 text-red-500"><Trash2 size={14} /></button>
              </div>
            ))}
          </div>
        </section>

        {/* Passengers (unlimited) */}
        <section className="bg-white border border-border rounded p-5 mb-5">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-sm font-semibold text-emerald-700 uppercase tracking-widest">Guests ({v.passengers.length})</h2>
            <button data-testid="v-add-pax" onClick={addPax} className="inline-flex items-center gap-1 text-xs text-[#16A34A] hover:underline"><Plus size={13} /> Add guest</button>
          </div>
          <div className="mb-4">
            <div className="text-[10px] uppercase tracking-widest text-slate-500 mb-1.5">Customer (from CRM)</div>
            <CustomerPicker
              value={v.customer_ref}
              onPick={(c) => {
                const nm = c.customer_type === "corporate" && c.company_name
                  ? c.company_name
                  : [c.title, c.first_name, c.middle_name, c.last_name].filter(Boolean).join(" ");
                setV((p) => {
                  const passengers = [...(p.passengers || [])];
                  if (passengers[0] && !passengers[0].name) {
                    passengers[0] = { ...passengers[0], name: nm };
                  }
                  return {
                    ...p,
                    customer_id: c.id,
                    customer_ref: { id: c.id, customer_id: c.customer_id, name: nm, email: c.email, mobile: c.mobile },
                    passengers,
                  };
                });
              }}
              onClear={() => setV((p) => ({ ...p, customer_id: "", customer_ref: null }))}
              testid="v-customer-picker"
            />
          </div>
          <div className="space-y-3">
            {v.passengers.map((p, i) => (
              <div key={i} className="grid grid-cols-12 gap-2 items-end border border-slate-100 rounded p-3 bg-slate-50/60">
                <div className="col-span-3">
                  <div className="text-[10px] uppercase tracking-widest text-slate-500 mb-1">Guest Name</div>
                  <input data-testid={`v-pax-name-${i}`} value={p.name} onChange={(e) => updPax(i, "name", e.target.value)} placeholder="e.g. Utkarsh Trivedi" className="w-full border border-border rounded px-2 py-1.5 text-sm" />
                </div>
                <div className="col-span-3">
                  <div className="text-[10px] uppercase tracking-widest text-slate-500 mb-1">Room Type</div>
                  <PickOrType
                    testid={`v-pax-room-${i}`}
                    options={ROOM_CATEGORIES}
                    value={p.room_type}
                    onChange={(val) => updPax(i, "room_type", val)}
                    placeholder="Select…"
                  />
                </div>
                <div className="col-span-2">
                  <div className="text-[10px] uppercase tracking-widest text-slate-500 mb-1">Inclusion</div>
                  <PickOrType
                    testid={`v-pax-incl-${i}`}
                    options={INCLUSION_OPTIONS}
                    value={p.room_inclusion}
                    onChange={(val) => updPax(i, "room_inclusion", val)}
                    placeholder="Select…"
                  />
                </div>
                <div className="col-span-2">
                  <div className="text-[10px] uppercase tracking-widest text-slate-500 mb-1">Confirmation</div>
                  <input data-testid={`v-pax-conf-${i}`} value={p.room_confirmation} onChange={(e) => updPax(i, "room_confirmation", e.target.value)} className="w-full border border-border rounded px-2 py-1.5 text-sm" />
                </div>
                <div className="col-span-1">
                  <div className="text-[10px] uppercase tracking-widest text-slate-500 mb-1">Pax</div>
                  <select data-testid={`v-pax-type-${i}`} value={p.pax_type} onChange={(e) => updPax(i, "pax_type", e.target.value)} className="w-full border border-border rounded px-1 py-1.5 text-sm">
                    <option>Adult</option><option>Child</option><option>Infant</option>
                  </select>
                </div>
                <div className="col-span-1 flex justify-end">
                  <button data-testid={`v-pax-rm-${i}`} onClick={() => rmPax(i)} className="p-2 text-red-500"><Trash2 size={13} /></button>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* Terms */}
        <section className="bg-white border border-border rounded p-5 mb-5">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-sm font-semibold text-emerald-700 uppercase tracking-widest">Terms & Conditions ({v.terms_conditions.length})</h2>
            <button onClick={addTerm} className="inline-flex items-center gap-1 text-xs text-[#16A34A] hover:underline"><Plus size={13} /> Add term</button>
          </div>
          <div className="space-y-2">
            {v.terms_conditions.map((t, i) => (
              <div key={i} className="flex items-start gap-2">
                <textarea data-testid={`v-term-${i}`} value={t} onChange={(e) => updTerm(i, e.target.value)} rows={2}
                  className="flex-1 border border-border rounded px-3 py-2 text-sm resize-none" />
                <button onClick={() => rmTerm(i)} className="p-2 text-red-500 mt-1"><Trash2 size={13} /></button>
              </div>
            ))}
          </div>
        </section>

        {/* Footer contact */}
        <section className="bg-white border border-border rounded p-5 mb-8">
          <h2 className="text-sm font-semibold text-emerald-700 mb-4 uppercase tracking-widest">Footer Contact (leave blank to use profile)</h2>
          <div className="grid grid-cols-2 gap-4">
            <Field label="Phone" value={v.footer_phone} onChange={(x) => set("footer_phone", x)} testid="v-footer-phone" />
            <Field label="Address" value={v.footer_address} onChange={(x) => set("footer_address", x)} testid="v-footer-addr" />
          </div>
        </section>

        <div className="flex justify-end gap-2 pb-12">
          <button onClick={() => save()} disabled={saving} className="inline-flex items-center gap-2 border border-emerald-600 text-emerald-700 px-4 py-2 rounded text-sm font-semibold hover:bg-emerald-50">
            <Save size={14} /> {saving ? "Saving…" : "Save"}
          </button>
          <button onClick={() => save({ open: true })} disabled={saving} className="inline-flex items-center gap-2 bg-[#16A34A] hover:bg-emerald-700 text-white px-4 py-2 rounded text-sm font-semibold">
            <FileDown size={14} /> Save & Download PDF
          </button>
        </div>
      </main>
    </div>
  );
}
